import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Blocks, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const ABOUT_MEDIA = "/about-image.jpg";

const pillars = [
  {
    Icon: Sparkles,
    title: "AI-accelerated delivery",
    body: "AI-assisted engineering and domain-led configuration.",
  },
  {
    Icon: Blocks,
    title: "Composable Integration",
    body: "API-first, modular integration architecture.",
  },
  {
    Icon: ShieldCheck,
    title: "Platform-Grade Reliability",
    body: "Enterprise-grade controls, governance and scalability.",
  },
];

export const About = () => {
  return (
    <section id="platform" data-testid="about-section" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-24 md:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT — Tower visual / video */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
            data-testid="about-visual"
          >
            <div className="relative aspect-[3/5] w-full max-w-[420px] mx-auto lg:mx-0 bg-cream border border-hairline overflow-hidden">
              <img
                src={ABOUT_MEDIA}
                alt="Gradatim infrastructure layer for modern insurance operations"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="about-image"
              />
              {/* Soft top hairline + bottom vignette */}
              <div aria-hidden className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
              <div aria-hidden className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white/60 to-transparent pointer-events-none" />
              {/* Small label tab */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 border border-hairline">
                <span className="h-1.5 w-1.5 bg-copper rounded-full" />
                <span className="overline text-slate-700">Infrastructure layer</span>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="mb-8">
              <span className="overline text-copper">About Gradatim</span>
            </div>

            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-ink">
              We don't just implement
              <br />
              <span className="text-slate-400">insurance software.</span>
            </h2>

            <h3 className="font-display text-3xl md:text-[40px] tracking-tight leading-[1.08] text-copper mt-8 max-w-xl">
              We build the infrastructure on which insurance businesses run.
            </h3>

            <p className="mt-8 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Gradatim is the <span className="text-ink font-medium">infrastructure layer</span> for
              modern insurance operations — a platform where{" "}
              <span className="text-ink">policy administration, claims, underwriting,
              reinsurance, accounting and distribution</span>{" "}
              connect, integrate, and scale.
            </p>

            {/* Pillars */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-hairline border border-hairline">
              {pillars.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.6 }}
                  className="group bg-white p-6 md:p-7 hover:bg-cream transition-colors"
                  data-testid={`about-pillar-${i}`}
                >
                  <div className="h-11 w-11 border border-hairline group-hover:border-copper flex items-center justify-center mb-5 transition-colors">
                    <p.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                  </div>
                  <h4 className="font-display text-lg md:text-xl text-ink leading-snug tracking-tight">
                    {p.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">{p.body}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-12">
              <Link
                to="/platform"
                data-testid="about-explore-cta"
                className="group inline-flex items-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                Explore platform
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
